import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { QUERY_KEY } from './keys';
import { clmApiClient } from '../clients';
import type { ApiErrorResponse, IDN } from '../types';
import { isApiErrorResponse } from '../utils';

const fetchIDNById = async (id: string | undefined): Promise<IDN> => {
  if (!id) {
    throw new Error('IDN id is required to fetch IDN data');
  }

  const response = await clmApiClient.get<IDN, ApiErrorResponse>(`idn/${id}`);

  if (!response.data) {
    throw new Error('No data returned from IDN fetch');
  }

  if (!response.ok) {
    if (isApiErrorResponse(response.data)) {
      throw new Error(response.data.details);
    }
    throw new Error('Unknown error');
  }

  return response.data;
};

export const useIDNGet = (
  id: string | undefined,
  config?: Omit<UseQueryOptions<IDN>, 'queryKey' | 'queryFn'>,
) =>
  useQuery({
    queryKey: [QUERY_KEY.IDN_GET, id],
    queryFn: () => fetchIDNById(id),
    enabled: !!id, // avoids running when id is null/undefined
    staleTime: 1000 * 60 * 5, // 5 minutes
    ...config,
  });
